import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import axios from 'axios';
import themeAssure from './theme';
import { racineApi } from './PageDepot';

const RACINE = racineApi();

const taille = (octets) => {
  if (octets < 1024 * 1024) return `${Math.max(1, Math.round(octets / 1024))} Ko`;
  if (octets < 1024 ** 3) return `${(octets / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`;
  return `${(octets / 1024 ** 3).toFixed(2).replace('.', ',')} Go`;
};

/**
 * La page qu'ouvre le destinataire d'un lien de téléchargement.
 *
 * Sans compte : le lien suffit. Elle ne montre que les pièces envoyées.
 */
export default function PageTransfert() {
  const { jeton } = useParams();
  const [transfert, setTransfert] = useState(null);
  const [erreur, setErreur] = useState('');

  const base = `${RACINE}/transferts/${jeton}`;

  useEffect(() => {
    axios.get(`${base}/`)
      .then(({ data }) => setTransfert(data))
      .catch((e) => setErreur(e?.response?.data?.detail
        || 'Impossible de contacter le serveur.'));
  }, [base]);

  return (
    <ThemeProvider theme={themeAssure}>
      <ScopedCssBaseline>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
          <Container maxWidth="sm">
            <Typography variant="h1" sx={{ mb: 1 }}>
              Pièces à télécharger
            </Typography>

            {erreur && <Alert severity="warning" sx={{ mb: 3 }}>{erreur}</Alert>}
            {!transfert && !erreur && <CircularProgress size={28} />}

            {transfert && (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Envoyées par {transfert.expediteur}
                  {transfert.objet ? ` — « ${transfert.objet} »` : ''}.
                  {' '}Disponibles jusqu'au{' '}
                  {new Date(transfert.expire_le).toLocaleDateString('fr-BE')}.
                </Typography>

                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    {transfert.pieces.length > 1 && (
                      <Button variant="contained" size="large" fullWidth
                              href={`${base}/tout/`}>
                        Tout télécharger ({taille(transfert.taille)})
                      </Button>
                    )}
                    <List disablePadding>
                      {transfert.pieces.map((piece) => (
                        <ListItem key={piece.id} disableGutters divider
                                  secondaryAction={(
                                    <Button size="small"
                                            variant={transfert.pieces.length > 1
                                              ? 'text' : 'contained'}
                                            href={`${base}/pieces/${piece.id}/`}>
                                      Télécharger
                                    </Button>
                                  )}>
                          <ListItemText
                            primary={piece.nom}
                            secondary={taille(piece.taille)}
                            primaryTypographyProps={{ sx: { pr: 12, wordBreak: 'break-word' } }} />
                        </ListItem>
                      ))}
                    </List>
                  </Stack>
                </Paper>
              </>
            )}
          </Container>
        </Box>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}
